'use client';

import type { PostHog as Posthog_Client } from 'posthog-js';
import { deleteCookie, setCookie } from 'cookies-next';
import { Time } from '@funkey/shared';
import type { PostHog_Features } from './shared';
import { PostHog_OVERRIDE_COOKIE, PostHog_OVERRIDES, PostHog_parseOverrides } from './shared';
import { _POSTHOG_SHARED_CONFIG } from '../shared';

/** Where the PostHog client stores the flags the toolbar has forced. */
const POSTHOG_OVERRIDE_PROPERTY = '$override_feature_flags';

export class PostHog_ClientFeaturesSDK {
    static getFlag<T extends PostHog_Features.Name>(
        client: Posthog_Client,
        key: T,
        fallback?: PostHog_Features.Variant<T>,
    ): PostHog_Features.Variant<T> | null {
        const overridden = PostHog_ClientFeaturesSDK.getOverride(client, key);
        if (overridden) return overridden;

        // With analytics off the server cannot bucket the visitor and nothing is recorded, so the
        // bucketed variant would only disagree with the page the server rendered. Overrides apply.
        if (!_POSTHOG_SHARED_CONFIG.ENABLED || !client?.__loaded) {
            return fallback ?? null;
        }

        const flagValue = client.getFeatureFlag(key) as PostHog_Features.Variant<T> | undefined;
        return flagValue ?? fallback ?? null;
    }

    /** The variant someone forced by hand, from the PostHog toolbar or from
     * `PostHog_OVERRIDES` — as opposed to the one PostHog bucketed this visitor
     * into. The server reads the same overrides from `PostHog_OVERRIDE_COOKIE`. */
    static getOverride<T extends PostHog_Features.Name>(
        client: Posthog_Client,
        key: T,
    ): PostHog_Features.Variant<T> | null {
        if (PostHog_OVERRIDES[key]) return PostHog_OVERRIDES[key];

        return (
            PostHog_parseOverrides(client?.get_property?.(POSTHOG_OVERRIDE_PROPERTY))[key] ?? null
        );
    }

    /** Copies the toolbar overrides into `PostHog_OVERRIDE_COOKIE` so the server renders them
     * too, and removes the cookie once they are cleared. Run it from `onFeatureFlags`, which fires
     * when flags load and whenever the toolbar sets or clears an override. */
    static syncOverrideCookie(client: Posthog_Client): void {
        const overrides = PostHog_parseOverrides(client.get_property(POSTHOG_OVERRIDE_PROPERTY));

        if (Object.keys(overrides).length === 0) {
            deleteCookie(PostHog_OVERRIDE_COOKIE, { path: '/' });
            return;
        }
        setCookie(PostHog_OVERRIDE_COOKIE, JSON.stringify(overrides), {
            path: '/',
            maxAge: Time.seconds({ days: 30 }),
        });
    }
}
